import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HeaderComponent } from '../../../shared/header/header.component';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { TimerConfigService } from '../../../../services/timer-config/timer-config.service';
import { UserService } from '../../../../services/user/user.service';
import { SupervisorService } from '../../../../services/supervisor/supervisor.service';
import { QueueConfigService } from '../../../../services/queue-config/queue-config.service';
import { TimerConfigDto, AttendanceMetaDto } from '../../../../interfaces/timer-config.dto';
import { moverNivel, normalizarNiveis, validarNiveis } from '../../../../utils/particular-levels.util';

@Component({
  selector: 'app-timer-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent],
  templateUrl: './timer-settings.component.html',
})
export class TimerSettingsComponent implements OnInit {
  clinicId!: number;
  username!: string;

  timer: TimerConfigDto  = { clinicId: 0, timerSeconds: 5, autoPauseMinutes: 10, autoCallEnabled: true };
  meta: AttendanceMetaDto = { clinicId: 0, dailyTarget: 30, hourlyTarget: 5, maxAttendanceMinutes: 30 };

  guiches: { id: number; name: string }[] = [];

  savedTimer = false;
  savedMeta  = false;
  savedPriorizar = false;
  isLoading  = false;

  // getTimerConfig agora propaga falha de rede/API em vez de devolver defaults em silencio
  // (senão qualquer save aqui em cima gravaria a unidade inteira com timer/priorizar/niveis zerados).
  configLoaded = false;
  loadError: string | null = null;

  niveisParticular: string[] = [];
  operadores: string[] = [];
  novaOrdem = true;
  erroParticular: string | null = null;
  savedParticular = false;
  isSubmittingParticular = false;

  constructor(
    private timerConfigService: TimerConfigService,
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private supervisorService: SupervisorService,
    private queueConfigService: QueueConfigService,
  ) {
    this.clinicId = this.localStorageService.get('currentUnit');
    this.username = this.localStorageService.get('login');
  }

  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    try {
      const [timer, meta] = await Promise.all([
        this.timerConfigService.getTimerConfig(this.clinicId),
        this.timerConfigService.getMeta(this.clinicId),
      ]);
      this.timer = timer;
      this.meta = meta;
      this.niveisParticular = timer.particularLevels ?? [];
      this.configLoaded = true;
    } catch (error) {
      console.error('Erro ao carregar configuração da unidade:', error);
      this.loadError = 'Não foi possível carregar a configuração da unidade. Recarregue a página antes de salvar.';
      this.isLoading = false;
      return;
    }
    try {
      const tw = await (await this.userService.getTicketsWindows(String(this.clinicId))).toPromise();
      this.guiches = (tw?.data ?? []).map((c: any) => ({ id: c.id, name: c.name }));
    } catch (error) {
      console.error('Erro ao carregar guichês:', error);
      this.guiches = [];
    }
    const [operadores, queueConfig] = await Promise.all([
      this.supervisorService.getOperators(this.clinicId),
      this.queueConfigService.getQueueConfig(this.clinicId),
    ]);
    this.operadores = operadores;
    this.novaOrdem = queueConfig.novaOrdem;
    this.isLoading = false;
  }

  // Trava os tres saves que gravam a config inteira (timer/priorizar/niveis) quando a carga
  // inicial falhou - sem isso, um blip de rede no GET seguido de qualquer save apaga a
  // configuracao real da unidade com os defaults do objeto local.
  private podeSalvarConfig(): boolean {
    if (this.configLoaded) return true;
    this.loadError = 'Não foi possível carregar a configuração da unidade. Recarregue a página antes de salvar.';
    return false;
  }

  async saveTimer(): Promise<void> {
    if (!this.podeSalvarConfig()) return;
    await this.timerConfigService.saveTimerConfig(
      this.clinicId, this.timer.timerSeconds, this.timer.autoPauseMinutes, this.timer.autoCallEnabled,
      this.username, this.timer.hrInicioPriorizar, this.timer.hrFimPriorizar, this.timer.nrSeqComputadorPriorizar,
      this.niveisParticular);
    this.savedTimer = true;
    setTimeout(() => (this.savedTimer = false), 3000);
  }

  async savePriorizar(): Promise<void> {
    if (!this.podeSalvarConfig()) return;
    await this.timerConfigService.saveTimerConfig(
      this.clinicId, this.timer.timerSeconds, this.timer.autoPauseMinutes, this.timer.autoCallEnabled,
      this.username, this.timer.hrInicioPriorizar, this.timer.hrFimPriorizar, this.timer.nrSeqComputadorPriorizar,
      this.niveisParticular);
    this.savedPriorizar = true;
    setTimeout(() => (this.savedPriorizar = false), 3000);
  }

  async saveMeta(): Promise<void> {
    await this.timerConfigService.saveMeta(this.clinicId, {
      dailyTarget: this.meta.dailyTarget,
      hourlyTarget: this.meta.hourlyTarget,
      maxAttendanceMinutes: this.meta.maxAttendanceMinutes,
    }, this.username);
    this.savedMeta = true;
    setTimeout(() => (this.savedMeta = false), 3000);
  }

  addNivel(): void {
    this.niveisParticular = [...this.niveisParticular, ''];
  }

  removeNivel(index: number): void {
    this.niveisParticular = this.niveisParticular.filter((_, i) => i !== index);
    this.erroParticular = null;
  }

  subirNivel(index: number): void {
    this.niveisParticular = moverNivel(this.niveisParticular, index, index - 1);
  }

  descerNivel(index: number): void {
    this.niveisParticular = moverNivel(this.niveisParticular, index, index + 1);
  }

  // Login sem ciclo de atendimento nos ultimos 90 dias provavelmente esta errado — avisa, mas nao bloqueia.
  nivelDesconhecido(login: string): boolean {
    const norm = (login ?? '').trim().toUpperCase();
    return norm.length > 0 && this.operadores.length > 0 && !this.operadores.includes(norm);
  }

  async saveParticular(): Promise<void> {
    if (!this.podeSalvarConfig()) return;
    this.erroParticular = validarNiveis(this.niveisParticular);
    if (this.erroParticular) return;

    this.isSubmittingParticular = true;
    try {
      this.niveisParticular = normalizarNiveis(this.niveisParticular);
      await this.timerConfigService.saveTimerConfig(
        this.clinicId, this.timer.timerSeconds, this.timer.autoPauseMinutes, this.timer.autoCallEnabled,
        this.username, this.timer.hrInicioPriorizar, this.timer.hrFimPriorizar, this.timer.nrSeqComputadorPriorizar,
        this.niveisParticular);
      this.savedParticular = true;
      setTimeout(() => (this.savedParticular = false), 3000);
    } catch (error) {
      console.error('Erro ao salvar niveis de particular:', error);
      this.erroParticular = 'Nao foi possivel salvar. Tente novamente.';
    } finally {
      this.isSubmittingParticular = false;
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
}
